import React from "react"
import { useTranslation } from "react-i18next"

import { FlowDirectionTooltipContent } from "@/components/fields/flow-direction-tooltip-content"
import { useUserSettings } from "@/contexts"
import { useFormatDateRange } from "@/formatters"
import type { SelectableLanguageCode } from "@/models/i18n"
import { SiteBillCost } from "@/modules/site/components/site-bill-cost"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { displayResourceCode } from "@/services/bill/bill-resource"
import type { Bill } from "@/services/core-api/types"
import { useFormatFlowDirection } from "@/utils/bill/format-flow-direction"
import { PERIOD_DATE_FORMAT } from "@/utils/date"
import { formatUsage } from "@/utils/format-usage/format-usage"
import { getDisplayValue } from "@/utils/value"

/** The bill fields the summary reads, so a bill period can be shown too. */
type BillSummary = Pick<
  Bill,
  | "cost"
  | "currency"
  | "end_date"
  | "flow_direction"
  | "provider_name"
  | "resource_type"
  | "start_date"
  | "usage"
  | "usage_unit"
> &
  Partial<Pick<Bill, "is_locked" | "source_type">>

interface BillDetailReadonlyFieldsProps {
  bill: BillSummary
  meterValue: React.ReactNode
  showLocked?: boolean
  showSourceType?: boolean
  siteValue?: React.ReactNode
}

export const BillDetailReadonlyFields: React.FC<
  BillDetailReadonlyFieldsProps
> = ({
  bill,
  meterValue,
  showLocked = false,
  showSourceType = false,
  siteValue,
}) => {
  const { i18n, t } = useTranslation("common")
  const { formatDateRange } = useFormatDateRange(PERIOD_DATE_FORMAT)
  const { formatFlowDirection } = useFormatFlowDirection()
  const { unitsSystem } = useUserSettings()

  return (
    <>
      <ReadonlyField
        label={t("fields.utilityProvider.title")}
        value={getDisplayValue(bill.provider_name)}
      />
      {siteValue != null ? (
        <ReadonlyField label={t("fields.siteName.title")} value={siteValue} />
      ) : null}
      <ReadonlyField label={t("fields.meterCode.title")} value={meterValue} />
      <ReadonlyField
        label={t("fields.resource.title")}
        value={displayResourceCode(bill.resource_type, (key) => t(key))}
      />
      <ReadonlyField
        label={t("fields.period.title")}
        value={formatDateRange(bill.start_date, bill.end_date)}
      />
      <ReadonlyField
        label={t("fields.flowDirection.title")}
        tooltipContent={<FlowDirectionTooltipContent />}
        value={formatFlowDirection(bill.flow_direction)}
      />
      {showSourceType ? (
        <ReadonlyField
          label={t("fields.sourceType.title")}
          value={getDisplayValue(bill.source_type)}
        />
      ) : null}
      <ReadonlyField
        label={t("fields.usage.title")}
        value={formatUsage(
          bill.usage,
          bill.usage_unit,
          i18n.language as SelectableLanguageCode,
          unitsSystem,
          { precision: 2 },
          { convert: false }
        )}
      />
      <ReadonlyField
        label={t("fields.cost.title")}
        value={<SiteBillCost cost={bill.cost} currency={bill.currency} />}
      />
      {showLocked ? (
        <ReadonlyField
          label={t("admin.billDetail.lockedLabel")}
          value={
            bill.is_locked
              ? t("admin.billDetail.lockedYes")
              : t("admin.billDetail.lockedNo")
          }
        />
      ) : null}
    </>
  )
}
